"use client";

import { useState, useEffect, useRef } from 'react';
import Navigation from '@/components/Navigation';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useRouter } from 'next/navigation';
import { Loader2, Sparkles, Image as ImageIcon, Camera, MapPin, Tag, CheckCircle2, ShieldAlert, X } from 'lucide-react';
import { tokenStore } from '@/lib/api/client';
import { Badge } from '@/components/ui/badge';

const API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

const CONDITIONS = [
    { id: 'new', name: 'جديد' },
    { id: 'refurbished', name: 'مجدد / ممتاز' },
    { id: 'used', name: 'مستعمل' },
    { id: 'damaged', name: 'للتصليح / تالف' },
];

export default function NewPublicListingPage() {
    const { toast } = useToast();
    const router = useRouter();
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState<any[]>([]);

    // Form State
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('');
    const [condition, setCondition] = useState('used');
    const [price, setPrice] = useState('');
    const [location, setLocation] = useState('');
    const [contactNumber, setContactNumber] = useState('');
    const [images, setImages] = useState<string[]>([]);

    // AI State
    const [aiSuggestingCategory, setAiSuggestingCategory] = useState(false);
    const [aiSuggestingPrice, setAiSuggestingPrice] = useState(false);
    const [aiSuggestedPriceRange, setAiSuggestedPriceRange] = useState('');
    const [aiCheckingImage, setAiCheckingImage] = useState(false);
    const [imageFeedback, setImageFeedback] = useState<{ accepted: boolean; msg: string } | null>(null);

    useEffect(() => {
        // Check auth
        if (!tokenStore.getAccess()) {
            toast({ title: 'يرجى تسجيل الدخول أولاً', variant: 'destructive' });
            router.push('/login');
            return;
        }
        // Fetch categories
        fetch(`${API}/api/marketplace/public-listings/categories/`)
            .then(res => res.json())
            .then(data => {
                setCategories(data.results || data || []);
            }).catch(() => { });
    }, []);

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onloadend = async () => {
            const b64 = (reader.result as string).split(',')[1];

            // Run AI Image Check
            setAiCheckingImage(true);
            setImageFeedback(null);
            try {
                const res = await fetch(`${API}/api/ai/marketplace/check-image/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${tokenStore.getAccess()}`
                    },
                    body: JSON.stringify({ image_b64: b64 })
                });
                const data = await res.json();

                if (data.is_accepted === false) {
                    setImageFeedback({ accepted: false, msg: data.feedback });
                    return;
                } else {
                    setImageFeedback({ accepted: true, msg: data.feedback || "تم قبول الصورة بنجاح!" });
                    setImages(prev => [...prev, `data:image/jpeg;base64,${b64}`]);
                }
            } catch (err) {
                // Fallback: allow anyway if AI fails
                setImages(prev => [...prev, `data:image/jpeg;base64,${b64}`]);
            } finally {
                setAiCheckingImage(false);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleAiCategorySuggest = async () => {
        if (!title) {
            toast({ title: 'يرجى إدخال عنوان الإعلان أولاً', variant: 'destructive' });
            return;
        }
        setAiSuggestingCategory(true);
        try {
            const res = await fetch(`${API}/api/ai/marketplace/suggest-category/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${tokenStore.getAccess()}`
                },
                body: JSON.stringify({ title, description })
            });
            const data = await res.json();
            if (data.category_name) {
                toast({ title: '🤖 مقترح فئة المنتج', description: data.category_name });
                // Try to match with existing
                const matched = categories.find(c =>
                    data.category_name.toLowerCase().includes(c.name.toLowerCase()) ||
                    data.category_name.includes(c.name_ar) ||
                    (data.category_id && c.id.toString() === data.category_id.toString()) ||
                    (data.category_id && c.slug === data.category_id)
                );
                if (matched) setCategory(matched.id.toString());
            }
        } catch { } finally { setAiSuggestingCategory(false); }
    };

    const handleAiPriceSuggest = async () => {
        if (!title) {
            toast({ title: 'يرجى إدخال العنوان للحصول على تسعير', variant: 'destructive' });
            return;
        }
        setAiSuggestingPrice(true);
        try {
            const catObj = categories.find(c => c.id.toString() === category);
            const res = await fetch(`${API}/api/ai/marketplace/suggest-price/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${tokenStore.getAccess()}`
                },
                body: JSON.stringify({ title, condition, category_name: catObj?.name_ar || catObj?.name || '' })
            });
            const data = await res.json();
            if (data.suggested_price) {
                setPrice(data.suggested_price.toString());
                setAiSuggestedPriceRange(data.price_range);
                toast({ title: '🤖 تسعير ذكي مقترح', description: data.reasoning });
            }
        } catch { } finally { setAiSuggestingPrice(false); }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title || !category || !price) {
            toast({ title: 'يرجى تعبئة الحقول الأساسية', variant: 'destructive' });
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${API}/api/marketplace/public-listings/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${tokenStore.getAccess()}`
                },
                body: JSON.stringify({
                    title, description, category, condition, price, location, contact_number: contactNumber,
                    images: images.map(img => ({ image_b64: img.split(',')[1] }))
                })
            });

            if (res.ok) {
                toast({ title: '🎉 تم نشر إعلانك!', description: 'سيتم مراجعة الإعلان قريباً.' });
                router.push('/marketplace');
            } else {
                const err = await res.json();
                toast({ title: 'خطأ', description: err.detail || 'حدث خطأ أثناء النشر', variant: 'destructive' });
            }
        } catch (err) {
            toast({ title: 'خطأ بالاتصال', variant: 'destructive' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC]">
            <Navigation title="إضافة إعلان جديد" />

            <main className="max-w-3xl mx-auto p-4 py-8 pb-32">
                <div className="mb-8">
                    <h1 className="text-3xl font-black text-slate-800 tracking-tight">ماذا تود أن تبيع؟</h1>
                    <p className="text-slate-500 mt-2">أعلن عن أجهزتك، أثاثك، أو أي غرض آخر في ثوانٍ معدودة.</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <Card className="rounded-[2.5rem] border-none shadow-sm overflow-hidden bg-white">
                        <CardHeader className="bg-slate-50/50 border-b border-slate-100 pb-6 px-8 py-8">
                            <CardTitle className="text-xl font-black">التفاصيل الأساسية</CardTitle>
                            <CardDescription>اجذب المشترين بوصف وعنوان جذاب</CardDescription>
                        </CardHeader>
                        <CardContent className="p-8 space-y-6">
                            <div className="space-y-3">
                                <Label className="font-bold text-slate-700">عنوان الإعلان <span className="text-red-500">*</span></Label>
                                <Input
                                    placeholder="مثال: ايفون 15 برو ماكس 256 جيجا"
                                    className="bg-slate-50 border-slate-200 h-14 rounded-2xl text-lg font-medium"
                                    value={title} onChange={e => setTitle(e.target.value)}
                                />
                            </div>

                            <div className="space-y-3">
                                <Label className="font-bold text-slate-700">الوصف</Label>
                                <Textarea
                                    placeholder="اذكر حالة المنتج، مدة الاستخدام، الملحقات المتوفرة..."
                                    className="bg-slate-50 border-slate-200 min-h-[150px] rounded-2xl p-4 text-base"
                                    value={description} onChange={e => setDescription(e.target.value)}
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <Label className="font-bold text-slate-700">القسم <span className="text-red-500">*</span></Label>
                                        <Button
                                            type="button" variant="ghost" size="sm"
                                            className="h-8 px-3 text-xs text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-xl gap-2 font-bold"
                                            onClick={handleAiCategorySuggest} disabled={aiSuggestingCategory}
                                        >
                                            {aiSuggestingCategory ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                            اختيار ذكي
                                        </Button>
                                    </div>
                                    <Select value={category} onValueChange={setCategory}>
                                        <SelectTrigger className="bg-slate-50 border-slate-200 h-14 rounded-2xl font-medium">
                                            <SelectValue placeholder="اختر الفئة" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {categories.map(c => (
                                                <SelectItem key={c.id} value={c.id.toString()}>{c.name_ar}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-3">
                                    <Label className="font-bold text-slate-700">حالة المنتج <span className="text-red-500">*</span></Label>
                                    <Select value={condition} onValueChange={setCondition}>
                                        <SelectTrigger className="bg-slate-50 border-slate-200 h-14 rounded-2xl font-medium">
                                            <SelectValue placeholder="اختر الحالة" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {CONDITIONS.map(c => (
                                                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="rounded-[2.5rem] border-none shadow-sm overflow-hidden bg-white">
                        <CardHeader className="bg-slate-50/50 border-b border-slate-100 pb-6 px-8 py-8">
                            <CardTitle className="text-xl font-black">السعر والموقع</CardTitle>
                        </CardHeader>
                        <CardContent className="p-8 space-y-6">
                            <div className="flex flex-col md:flex-row gap-6">
                                <div className="flex-1 space-y-3">
                                    <div className="flex justify-between items-center">
                                        <Label className="font-bold text-slate-700">السعر (ر.س) <span className="text-red-500">*</span></Label>
                                        <Button
                                            type="button" variant="ghost" size="sm"
                                            className="h-8 px-3 text-xs text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-xl gap-2 font-bold"
                                            onClick={handleAiPriceSuggest} disabled={aiSuggestingPrice}
                                        >
                                            {aiSuggestingPrice ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                            مقترح ذكي
                                        </Button>
                                    </div>
                                    <div className="relative">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 font-black text-slate-300">SAR</span>
                                        <Input
                                            type="number" placeholder="0.00"
                                            className="bg-slate-50 border-slate-200 h-14 rounded-2xl text-xl font-black pl-14"
                                            value={price} onChange={e => setPrice(e.target.value)}
                                        />
                                    </div>
                                    {aiSuggestedPriceRange && (
                                        <p className="text-xs text-emerald-600 font-bold mt-2 bg-emerald-50 p-2 rounded-lg">
                                            💡 النطاق في السوق: {aiSuggestedPriceRange}
                                        </p>
                                    )}
                                </div>

                                <div className="flex-1 space-y-3">
                                    <Label className="font-bold text-slate-700">المدينة / الحي <span className="text-red-500">*</span></Label>
                                    <div className="relative">
                                        <MapPin className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                                        <Input
                                            placeholder="مثال: الرياض، النرجس"
                                            className="bg-slate-50 border-slate-200 h-14 rounded-2xl pr-12 font-medium"
                                            value={location} onChange={e => setLocation(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="rounded-[2.5rem] border-none shadow-sm overflow-hidden bg-white">
                        <CardHeader className="bg-slate-50/50 border-b border-slate-100 pb-4 px-8 py-8">
                            <div className="flex justify-between items-center">
                                <div>
                                    <CardTitle className="text-lg font-black">صور المنتج</CardTitle>
                                    <CardDescription>أضف حتى 5 صور واضحة</CardDescription>
                                </div>
                                {aiCheckingImage && <Badge variant="outline" className="bg-blue-50 text-blue-600 border-none px-3 py-1 gap-2 font-bold animate-pulse"><Loader2 className="w-3 h-3 animate-spin" /> جاري الفحص</Badge>}
                            </div>
                        </CardHeader>
                        <CardContent className="p-8">
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                                {images.map((img, i) => (
                                    <div key={i} className="relative aspect-square rounded-[1.5rem] overflow-hidden border-2 border-slate-100 shadow-sm group">
                                        <img src={img} alt="" className="w-full h-full object-cover" />
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <Button type="button" size="icon" variant="destructive" className="rounded-full w-8 h-8" onClick={() => setImages(images.filter((_, idx) => idx !== i))}>
                                                <X className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}

                                {images.length < 5 && (
                                    <label className="aspect-square rounded-[1.5rem] border-2 border-dashed border-slate-200 bg-slate-50 hover:bg-slate-100 transition-all flex flex-col items-center justify-center cursor-pointer text-slate-400 hover:text-primary hover:border-primary/30">
                                        <Camera className="w-10 h-10 mb-2 opacity-50" />
                                        <span className="text-xs font-bold">أضف صورة</span>
                                        <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} disabled={aiCheckingImage} />
                                    </label>
                                )}
                            </div>

                            {imageFeedback && (
                                <div className={`p-4 rounded-2xl flex items-start gap-3 mt-4 ${imageFeedback.accepted ? 'bg-emerald-50 text-emerald-800 border border-emerald-100' : 'bg-red-50 text-red-800 border border-red-100'}`}>
                                    {imageFeedback.accepted ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> : <ShieldAlert className="w-5 h-5 text-red-500 shrink-0" />}
                                    <p className="text-sm font-bold leading-relaxed">{imageFeedback.msg}</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <div className="pt-6 flex flex-col sm:flex-row gap-4">
                        <Button type="submit" disabled={loading} className="flex-1 h-16 rounded-[1.5rem] bg-emerald-600 hover:bg-emerald-700 text-white text-xl font-black shadow-xl shadow-emerald-500/20 transition-all">
                            {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : 'انشر إعلانك الآن'}
                        </Button>
                        <Button type="button" variant="ghost" onClick={() => router.back()} className="h-16 px-10 rounded-[1.5rem] font-bold text-slate-500 hover:bg-slate-100">
                            رجوع
                        </Button>
                    </div>
                </form>
            </main>
        </div>
    );
}
